import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthContext';
import { fetchStatsToday } from '../../lib/api/stats';
import { ThemeToggle } from './ThemeToggle';

/**
 * Avatar menu (UX-10) — replaces the old header cluster (ThemeToggle +
 * StatsBadge + email + Sign out button) with a single popover reached from
 * an avatar button showing the operator's initials.
 *
 * Follows the same close-on-outside-click / Escape / route-change pattern
 * as the `NavDropdown` used for the Workspace/Reports menus in Header.tsx,
 * plus roving up/down-arrow focus across its menu items and focus-return
 * to the avatar on Escape.
 */
export const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  // Only fetch today's stats once the menu is actually open.
  const statsQuery = useQuery({
    queryKey: ['stats-today'],
    queryFn: fetchStatsToday,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Auto-close on route change — mirrors NavDropdown in Header.tsx.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Move focus to the first item whenever the menu opens.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  if (!user?.email) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = itemRefs.current.filter((el): el is HTMLElement => el !== null);
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    e.preventDefault();
    if (e.key === 'ArrowDown') {
      const next = items[(currentIndex + 1 + items.length) % items.length];
      next.focus();
    } else {
      const prevIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
      items[prevIndex].focus();
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const initials = initialsFromEmail(user.email);
  const stats = statsQuery.data;
  const statsLabel = statsQuery.isLoading
    ? 'Today: loading…'
    : stats
      ? `Today: ${stats.runsToday} sessions · $${stats.costTodayUsd.toFixed(2)}`
      : 'Today';

  return (
    <div className="relative" ref={rootRef} data-testid="user-menu">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        data-testid="user-menu-trigger"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full
                   bg-gradient-to-br from-primary-400 to-primary-600 text-white
                   text-xs font-semibold shadow-card hover:brightness-105
                   transition-[filter] duration-150"
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40"
        >
          <div className="px-3 py-2 border-b border-slate-100" role="none">
            <div className="text-sm font-medium text-slate-900 truncate" title={user.email}>
              {user.email}
            </div>
          </div>

          <Link
            to="/today"
            role="menuitem"
            tabIndex={-1}
            ref={(el) => {
              itemRefs.current[0] = el;
            }}
            data-testid="user-menu-item-today"
            className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            {statsLabel}
          </Link>

          <div className="px-3 py-2 flex items-center justify-between gap-2" role="none">
            <span className="text-xs text-slate-500">Theme</span>
            <ThemeToggle />
          </div>

          <div className="border-t border-slate-100 my-1" role="none" />

          <Link
            to="/account/plan"
            role="menuitem"
            tabIndex={-1}
            ref={(el) => {
              itemRefs.current[1] = el;
            }}
            data-testid="user-menu-item-plan"
            className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Account &amp; plan
          </Link>

          <Link
            to="/account/members"
            role="menuitem"
            tabIndex={-1}
            ref={(el) => {
              itemRefs.current[2] = el;
            }}
            data-testid="user-menu-item-members"
            className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Members
          </Link>

          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            ref={(el) => {
              itemRefs.current[3] = el;
            }}
            onClick={handleLogout}
            data-testid="user-menu-item-signout"
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Two-letter initials from an email's local part. Splits on `. _ + -`
 * (e.g. "victor.gorbachev" → "VG"); with no separator, falls back to the
 * first two characters (e.g. "vgorbachev" → "VG").
 */
function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const tokens = local.split(/[._+-]+/).filter(Boolean);
  if (tokens.length >= 2) return (tokens[0][0] + tokens[1][0]).toUpperCase();
  if (tokens.length === 1 && tokens[0].length >= 2) return tokens[0].slice(0, 2).toUpperCase();
  if (tokens.length === 1 && tokens[0].length === 1) return tokens[0].toUpperCase();
  return '?';
}

export default UserMenu;
